import { ActiveCategory } from 'src/components/organisms/ActiveCategoriesSection/ActiveCategoryCard';
import { FormData, FormStatusTab, FormType } from './types';

interface CategoryAccumulator {
  activeCount: number;
  forms: FormData[];
}

function pickPrimaryForm(forms: FormData[]): FormData | null {
  if (forms.length === 0) {
    return null;
  }
  const active = forms.filter((f) => f.status === FormStatusTab.ACTIVE);
  const pool = active.length > 0 ? active : forms;
  return [...pool].sort((a, b) => a.title.localeCompare(b.title))[0] ?? null;
}

export function groupFormsByCategory(
  forms: FormData[],
  formType: FormType,
): ActiveCategory[] {
  const filtered = forms.filter((f) => f.type === formType);

  const categoryMap = new Map<string, CategoryAccumulator>();

  for (const form of filtered) {
    const categoryName = form.category || 'Uncategorized';
    const entry = categoryMap.get(categoryName) ?? {
      activeCount: 0,
      forms: [] as FormData[],
    };

    entry.forms.push(form);

    if (form.status === FormStatusTab.ACTIVE) {
      entry.activeCount += 1;
    }

    categoryMap.set(categoryName, entry);
  }

  return Array.from(categoryMap.entries()).map(
    ([name, { activeCount, forms: categoryForms }]) => {
      const primary = pickPrimaryForm(categoryForms);
      return {
        id: name,
        name,
        description: primary?.description?.trim()
          ? primary.description
          : undefined,
        languages: primary?.languages?.trim() ? primary.languages : undefined,
        activeFormsCount: activeCount,
        primaryForm: primary
          ? {
              id: primary.id,
              type: primary.type,
              status: primary.status,
            }
          : undefined,
      } satisfies ActiveCategory;
    },
  );
}

export function groupSpeakUpFormsByCategory(
  forms: FormData[],
): ActiveCategory[] {
  return groupFormsByCategory(forms, FormType.SPEAK_UP);
}

export function getPublishedSpeakUpForms(forms: FormData[]): ActiveCategory[] {
  const filtered = forms.filter(
    (f) => f.type === FormType.SPEAK_UP && f.status === FormStatusTab.ACTIVE,
  );

  return filtered.map((form) => ({
    id: form.id,
    name: form.title,
    description: form.description?.trim() ? form.description : undefined,
    languages: form.languages?.trim() ? form.languages : undefined,
    activeFormsCount: 1,
    primaryForm: {
      id: form.id,
      type: form.type,
      status: form.status,
    },
  }));
}

export function getPublishedCoiForms(forms: FormData[]): ActiveCategory[] {
  const filtered = forms.filter(
    (f) =>
      f.type === FormType.CONFLICT_OF_INTEREST &&
      f.status === FormStatusTab.ACTIVE,
  );

  return filtered.map((form) => ({
    id: form.id,
    name: form.title,
    description: form.description?.trim() ? form.description : undefined,
    languages: form.languages?.trim() ? form.languages : undefined,
    activeFormsCount: 1,
    primaryForm: {
      id: form.id,
      type: form.type,
      status: form.status,
    },
  }));
}
